import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ActiveSessionProvider } from "@/context/ActiveSessionContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Home from "@/pages/Home";
import Inquiries from "@/pages/Inquiries";
import InquiryDetail from "@/pages/InquiryDetail";
import InquiryOverview from "@/pages/InquiryOverview";
import ComplianceOverview from "@/pages/ComplianceOverview";
import ComplianceSetup from "@/pages/ComplianceSetup";
import ObligationsBoard from "@/pages/ObligationsBoard";
import VaultBoard from "@/pages/vault/VaultBoard";
import VaultFile from "@/pages/vault/VaultFile";
import VaultRegister from "@/pages/vault/VaultRegister";
import VaultLocations from "@/pages/vault/VaultLocations";
import VaultReports from "@/pages/vault/VaultReports";
import ComplianceExtensions from "@/pages/ComplianceExtensions";
import InquiryFollowups from "@/pages/InquiryFollowups";
import InquirySettings from "@/pages/InquirySettings";
import Contacts from "@/pages/Contacts";
import { isManager, hasModule } from "@/lib/access";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Obligations from "@/pages/Obligations";
import Categories from "@/pages/Categories";
import Settings from "@/pages/Settings";
import AdminUsers from "@/pages/AdminUsers";
import InviteAccept from "@/pages/InviteAccept";
import Tasks from "@/pages/Tasks";
import TaskDetail from "@/pages/TaskDetail";
import TaskDashboard from "@/pages/TaskDashboard";
import MasterSettings from "@/pages/MasterSettings";
import Timesheet from "@/pages/Timesheet";
import UserGuide from "@/pages/UserGuide";
import FilingBoard from "@/pages/FilingBoard";
import SuperTenants from "@/pages/SuperTenants";
import Utilisation from "@/pages/Utilisation";
import Migration from "@/pages/Migration";
import ClientPortalLinks from "@/pages/ClientPortalLinks";
import ClientPortal from "@/pages/ClientPortal";
import AuthCallback from "@/pages/AuthCallback";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function ManagerOnly({ children }) {
  const { user } = useAuth();
  if (user && !isManager(user)) return <Navigate to="/" replace />;
  return children;
}

function NeedsModule({ module, orShared, children }) {
  const { user } = useAuth();
  if (user && !hasModule(user, module) && !(orShared && user[orShared])) return <Navigate to="/" replace />;
  return children;
}

function SuperOnly({ children }) {
  const { user } = useAuth();
  const bootstrap = !!user?.is_super;
  if (user && (user.role !== "admin" || !bootstrap)) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/invite/:token" element={<InviteAccept />} />
      <Route path="/portal/:token" element={<ClientPortal />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Home />} />
        <Route path="inquiries" element={<NeedsModule module="inquiries"><Inquiries /></NeedsModule>} />
        <Route path="inquiries/overview" element={<NeedsModule module="inquiries"><InquiryOverview /></NeedsModule>} />
        <Route path="inquiries/followups" element={<NeedsModule module="inquiries"><InquiryFollowups /></NeedsModule>} />
        <Route path="inquiries/settings" element={<AdminOnly><InquirySettings /></AdminOnly>} />
        <Route path="inquiries/:id" element={<NeedsModule module="inquiries"><InquiryDetail /></NeedsModule>} />
        <Route path="contacts" element={<NeedsModule module="inquiries"><Contacts /></NeedsModule>} />
        <Route path="overview" element={<NeedsModule module="obligations"><Dashboard /></NeedsModule>} />
        <Route path="obligations" element={<NeedsModule module="obligations"><Obligations key="regular" kind="regular" /></NeedsModule>} />
        <Route path="categories" element={<NeedsModule module="obligations"><Categories /></NeedsModule>} />
        <Route path="settings" element={<Settings />} />
        <Route path="master-settings" element={<MasterSettings />} />
        <Route path="tasks" element={<NeedsModule module="tasks"><Tasks /></NeedsModule>} />
        <Route path="tasks/dashboard" element={<NeedsModule module="tasks"><TaskDashboard /></NeedsModule>} />
        <Route path="tasks/:id" element={<NeedsModule module="tasks"><TaskDetail /></NeedsModule>} />
        <Route path="timesheet" element={<NeedsModule module="tasks"><Timesheet /></NeedsModule>} />
        <Route path="guide" element={<UserGuide />} />
        <Route path="filing-board" element={<NeedsModule module="compliance"><FilingBoard /></NeedsModule>} />
        <Route path="obligations/board" element={<NeedsModule module="obligations"><ObligationsBoard /></NeedsModule>} />
        <Route path="vault" element={<NeedsModule module="vault" orShared="vault_shared"><VaultBoard /></NeedsModule>} />
        <Route path="vault/files/:id" element={<NeedsModule module="vault" orShared="vault_shared"><VaultFile /></NeedsModule>} />
        <Route path="vault/register" element={<NeedsModule module="vault"><VaultRegister /></NeedsModule>} />
        <Route path="vault/locations" element={<NeedsModule module="vault"><VaultLocations /></NeedsModule>} />
        <Route path="vault/reports" element={<NeedsModule module="vault"><VaultReports /></NeedsModule>} />
        <Route path="compliance" element={<NeedsModule module="compliance"><ComplianceOverview /></NeedsModule>} />
        <Route path="compliance/register" element={<NeedsModule module="compliance"><Obligations key="compliance" kind="compliance" /></NeedsModule>} />
        <Route path="compliance/invoices" element={<NeedsModule module="compliance"><FilingBoard key="invoices" initialTab="register" /></NeedsModule>} />
        <Route path="compliance/extensions" element={<NeedsModule module="compliance"><ManagerOnly><ComplianceExtensions /></ManagerOnly></NeedsModule>} />
        <Route path="compliance/setup" element={<NeedsModule module="compliance"><ComplianceSetup /></NeedsModule>} />
        <Route path="admin/users" element={<ManagerOnly><AdminUsers /></ManagerOnly>} />
        <Route path="admin/utilisation" element={<ManagerOnly><NeedsModule module="tasks"><Utilisation /></NeedsModule></ManagerOnly>} />
        <Route path="admin/migration" element={<AdminOnly><Migration /></AdminOnly>} />
        <Route path="admin/portal" element={<AdminOnly><ClientPortalLinks /></AdminOnly>} />
        <Route path="superadmin/tenants" element={<SuperOnly><SuperTenants /></SuperOnly>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <ActiveSessionProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
      </ActiveSessionProvider>
    </AuthProvider>
  );
}

export default App;
